import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import DataTable from './Common/DataTable';
import Modal from './Common/Modal';
import { useAuth } from '../contexts/AuthContext';

const ROLE_OPTIONS = ['admin', 'superintendent', 'principal', 'teacher', 'parent', 'accountant', 'librarian', 'student', 'receptionist', 'lab_attendant',];

const ROLE_META = {
  admin:          { label: 'Admins',          icon: 'fa-user-shield',         color: 'text-red-600',     border: 'border-red-500',     bg: 'bg-red-50' },
  superintendent: { label: 'Superintendents', icon: 'fa-user-tie',            color: 'text-purple-600',  border: 'border-purple-500',  bg: 'bg-purple-50' },
  principal:      { label: 'Principals',      icon: 'fa-user-graduate',       color: 'text-blue-600',    border: 'border-blue-500',    bg: 'bg-blue-50' },
  teacher:        { label: 'Teachers',        icon: 'fa-chalkboard-teacher',  color: 'text-green-600',   border: 'border-green-500',   bg: 'bg-green-50' },
  parent:         { label: 'Parents',         icon: 'fa-user-friends',        color: 'text-yellow-600',  border: 'border-yellow-500',  bg: 'bg-yellow-50' },
  accountant:     { label: 'Accountants',     icon: 'fa-coins',               color: 'text-indigo-600',  border: 'border-indigo-500',  bg: 'bg-indigo-50' },
  librarian:      { label: 'Librarians',      icon: 'fa-book',                color: 'text-pink-600',    border: 'border-pink-500',    bg: 'bg-pink-50' },
  receptionist:   { label: 'Receptionists',   icon: 'fa-headset',             color: 'text-teal-600',    border: 'border-teal-500',    bg: 'bg-teal-50' },   
  lab_attendant:  { label: 'Lab Attendants',  icon: 'fa-flask',               color: 'text-cyan-600',    border: 'border-cyan-500',    bg: 'bg-cyan-50' },
  student:        { label: 'Students',        icon: 'fa-user-graduate',       color: 'text-gray-600',    border: 'border-gray-500',    bg: 'bg-gray-50' },
};

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const viewerRole = currentUser?.role;

  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_role: {} });
  const [campuses, setCampuses] = useState([]);
  const [students, setStudents] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState('all');
  const [filterCampus, setFilterCampus] = useState('all');
  const [search, setSearch] = useState('');
  const [resetModal, setResetModal] = useState(null);

  // ===== Which role cards to show per viewer =====
  const visibleRoleCards = useMemo(() => {
  if (viewerRole === 'accountant') {
    return ['teacher', 'parent', 'accountant', 'librarian', 'student', 'receptionist', 'lab_attendant'];
  }
  if (viewerRole === 'principal') {
    return ['principal', 'teacher', 'parent', 'accountant', 'librarian', 'student', 'receptionist', 'lab_attendant'];
  }
  if (viewerRole === 'superintendent') {
    return ['superintendent', 'principal', 'teacher', 'parent', 'accountant', 'librarian', 'student', 'receptionist', 'lab_attendant'];
  }
  return ['superintendent', 'principal', 'teacher', 'parent', 'accountant', 'librarian', 'student', 'receptionist', 'lab_attendant'];
}, [viewerRole]);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [usersRes, statsRes, campusRes, studentsRes, linksRes] = await Promise.all([
        api.get('/users/'),
        api.get('/users/stats'),
        api.get('/campuses/'),
        api.get('/students/').catch(() => ({ data: [] })),
        api.get('/parent-students/').catch(() => ({ data: [] })),
      ]);
      setUsers(usersRes.data || []);
      setStats(statsRes.data || {});
      setCampuses(campusRes.data || []);
      setStudents(studentsRes.data || []);
      setLinks(linksRes.data || []);
    } catch (err) {
      console.error('Failed to load users:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════════
  // Link lookups
  // ══════════════════════════════════════════════════════════
  const { parentsByStudentUserId, studentsByParentId } = useMemo(() => {
    const userById = {};
    users.forEach(u => { userById[u.id] = u; });

    const studentById = {};
    students.forEach(s => { studentById[s.id] = s; });

    const parentsByStudentUserId = {};
    const studentsByParentId = {};

    links.forEach(l => {
      const student = studentById[l.student_id];
      const parent = userById[l.parent_id];

      if (student) {
        if (!parentsByStudentUserId[student.user_id]) parentsByStudentUserId[student.user_id] = [];
        if (parent) parentsByStudentUserId[student.user_id].push({
          ...parent,
          relationship: l.relationship,
          is_primary: l.is_primary,
        });
      }

      if (parent) {
        if (!studentsByParentId[parent.id]) studentsByParentId[parent.id] = [];
        if (student) {
          const studentUser = userById[student.user_id];
          studentsByParentId[parent.id].push({
            ...student,
            name: studentUser ? `${studentUser.first_name} ${studentUser.last_name}` : `Student #${student.id}`,
            class_name: student.class_name,
            relationship: l.relationship,
            is_primary: l.is_primary,
          });
        }
      }
    });

    return { parentsByStudentUserId, studentsByParentId };
  }, [users, students, links]);

  const getLinkedTo = (u) => {
    if (u.role === 'parent') {
      const kids = studentsByParentId[u.id] || [];
      if (kids.length === 0) {
        return <span className="text-xs text-gray-400 italic">No children linked</span>;
      }
      return (
        <div className="flex flex-col gap-0.5">
          {kids.map((k, i) => (
            <div key={i} className="text-xs flex items-center gap-1">
              <i className="fas fa-child text-green-500 text-[10px]"></i>
              <span className="font-medium text-gray-800">{k.name}</span>
              {k.class_name && <span className="text-gray-500">· {k.class_name}</span>}
              {k.relationship && <span className="text-gray-400 text-[10px]">({k.relationship})</span>}
            </div>
          ))}
        </div>
      );
    }

    if (u.role === 'student') {
      const parents = parentsByStudentUserId[u.id] || [];
      if (parents.length === 0) {
        return <span className="text-xs text-gray-400 italic">No parent linked</span>;
      }
      return (
        <div className="flex flex-col gap-0.5">
          {parents.map((p, i) => (
            <div key={i} className="text-xs flex items-center gap-1">
              <i className="fas fa-user-friends text-yellow-600 text-[10px]"></i>
              <span className="font-medium text-gray-800">{p.first_name} {p.last_name}</span>
              {p.relationship && <span className="text-gray-400 text-[10px]">({p.relationship})</span>}
              {p.is_primary && (
                <span className="text-[9px] bg-blue-100 text-blue-700 px-1 rounded">Primary</span>
              )}
            </div>
          ))}
        </div>
      );
    }

    return <span className="text-xs text-gray-400">—</span>;
  };

  // ══════════════════════════════════════════════════════════
  // ✅ Click-card-to-filter logic
  // ══════════════════════════════════════════════════════════
  const handleCardClick = (role) => {
    // Clicking the already-active card clears the filter
    if (filterRole === role) {
      setFilterRole('all');
    } else {
      setFilterRole(role);
    }
  };

  // ══════════════════════════════════════════════════════════
  // Actions
  // ══════════════════════════════════════════════════════════
  const toggleStatus = async (userId, currentStatus) => {
    try {
      // We send the OPPOSITE of the current status to toggle it
      await api.put(`/users/${userId}`, { 
        is_active: !currentStatus 
      });
      fetchAll(); // Refresh the list after successful update
    } catch (err) {
      console.error("Status update error:", err);
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const resetPassword = async (userId, newPassword) => {
    try {
      await api.post(`/users/${userId}/reset-password?new_password=${newPassword}`);
      alert('Password reset successfully');
      setResetModal(null);
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const updateRole = async (userId, newRole) => {
    try {
      await api.put(`/users/${userId}`, { role: newRole });
      fetchAll();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  // ===== FILTER =====
  const filtered = users.filter(u => {
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (filterCampus !== 'all') {
      const campusName = campuses.find(c => c.id === parseInt(filterCampus))?.name;
      if (u.campus_name !== campusName) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      const blob = `${u.first_name} ${u.last_name} ${u.email} ${u.cnic || ''} ${u.phone || ''}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  const columns = ['ID', 'Name', 'Email', 'Phone', 'CNIC', 'Role', 'Campus', 'Linked To', 'Status', 'Actions'];

  const data = filtered.map((u) => ({
    ID: u.id,
    Name: `${u.first_name} ${u.last_name}`,
    Email: u.email,
    Phone: u.phone || '—',
    CNIC: u.cnic || '—',
    Role: isAdmin ? (
      <select
        value={u.role}
        onChange={(e) => updateRole(u.id, e.target.value)}
        className="px-2 py-1 text-xs border rounded bg-white"
      >
        {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
      </select>
    ) : (
      <span className="px-2 py-1 text-xs bg-gray-100 rounded">{u.role}</span>
    ),
    Campus: u.campus_name || '—',
    'Linked To': getLinkedTo(u),
    Status: (
      <span className={`px-2 py-1 rounded text-xs ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {u.is_active ? 'Active' : 'Inactive'}
      </span>
    ),
    Actions: (
      <div className="flex gap-2">
        {isAdmin ? (
          <>
            <button
              onClick={() => toggleStatus(u.id, u.is_active)}
              disabled={u.id === currentUser?.id}
              title={u.id === currentUser?.id ? "You can't deactivate yourself" : ""}
              className={`px-2 py-1 text-xs rounded ${
                u.id === currentUser?.id
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : u.is_active
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-800'
              }`}
            >
              {u.is_active ? 'Deactivate' : 'Activate'}
            </button>
            <button
              onClick={() => setResetModal({ userId: u.id, email: u.email })}
              className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded"
            >
              Reset Pwd
            </button>
          </>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </div>
    ),
  }));

  const isFiltering = filterRole !== 'all';

  return (
    <div>
      {viewerRole === 'accountant' && (
        <div className="bg-blue-50 border border-blue-200 p-3 rounded mb-4">
          <p className="text-sm text-blue-700">
            <i className="fas fa-info-circle"></i>{' '}
            You are viewing users from your campus only. Upper-level accounts
            (Admin, Superintendent, Principal) are managed by the school administration.
          </p>
        </div>
      )}

      {viewerRole === 'principal' && (
        <div className="bg-blue-50 border border-blue-200 p-3 rounded mb-4">
          <p className="text-sm text-blue-700">
            <i className="fas fa-info-circle"></i>{' '}
            You are viewing users from your campus only.
          </p>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          ROLE SUMMARY CARDS — now clickable filters
      ══════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        {/* Total Users card — clicking clears all role filters */}
        <button
          type="button"
          onClick={() => setFilterRole('all')}
          title="Show all roles"
          className={`text-left bg-white p-3 rounded shadow border-l-4 transition-all ${
            !isFiltering
              ? 'border-gray-700 ring-2 ring-gray-300'
              : 'border-gray-300 hover:border-gray-500'
          }`}
        >
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <i className="fas fa-users text-gray-700"></i> Total Users
          </p>
          <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
          {!isFiltering && (
            <p className="text-[10px] text-gray-500 mt-1">
              <i className="fas fa-check-circle"></i> Showing all
            </p>
          )}
        </button>

        {Object.entries(ROLE_META)
          .filter(([role]) => visibleRoleCards.includes(role))
          .map(([role, meta]) => {
            const count = stats.by_role?.[role] || 0;
            const isActive = filterRole === role;
            return (
              <button
                key={role}
                type="button"
                onClick={() => handleCardClick(role)}
                title={isActive ? `Click to clear filter` : `Click to filter by ${meta.label}`}
                className={`text-left bg-white p-3 rounded shadow border-l-4 transition-all ${
                  isActive
                    ? `${meta.border} ring-2 ring-offset-1 ring-gray-300 ${meta.bg}`
                    : `${meta.border.replace('500', '200')} hover:${meta.border} hover:shadow-md cursor-pointer`
                }`}
              >
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <i className={`fas ${meta.icon} ${meta.color}`}></i> {meta.label}
                </p>
                <p className={`text-2xl font-bold ${meta.color}`}>{count}</p>
                {isActive && (
                  <p className={`text-[10px] mt-1 ${meta.color} font-semibold`}>
                    <i className="fas fa-filter"></i> Filter active — click to clear
                  </p>
                )}
              </button>
            );
          })}
      </div>

      {/* ===== FILTER BAR ===== */}
      <div className="bg-white p-3 rounded shadow mb-4 flex flex-wrap gap-3 items-center">
        <select
          className="p-2 border rounded text-sm"
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
        >
          <option value="all">All Roles</option>
          {ROLE_OPTIONS
            .filter(r => visibleRoleCards.includes(r))
            .map(r => <option key={r} value={r}>{r}</option>)}
        </select>

        <select
          className="p-2 border rounded text-sm"
          value={filterCampus}
          onChange={(e) => setFilterCampus(e.target.value)}
        >
          <option value="all">All Campuses</option>
          {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <input
          type="text"
          placeholder="Search by name, email, phone, CNIC..."
          className="flex-1 min-w-[200px] p-2 border rounded text-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <button
          onClick={fetchAll}
          className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
        >
          <i className="fas fa-sync-alt mr-1"></i> Refresh
        </button>

        <span className="ml-auto text-sm text-gray-500">
          Showing {filtered.length} of {users.length}
        </span>
      </div>

      {/* Active filter chip */}
      {isFiltering && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-xs text-gray-500">Active filter:</span>
          <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
            ROLE_META[filterRole]?.bg || 'bg-gray-100'
          } ${ROLE_META[filterRole]?.color || 'text-gray-700'}`}>
            <i className={`fas ${ROLE_META[filterRole]?.icon || 'fa-filter'}`}></i>
            {ROLE_META[filterRole]?.label || filterRole}
            <button
              onClick={() => setFilterRole('all')}
              className="ml-1 hover:opacity-70 font-bold"
              title="Clear filter"
            >×</button>
          </span>
        </div>
      )}

      {/* ===== USERS TABLE ===== */}
      <div className="bg-white p-4 rounded shadow">
        <h2 className="text-lg font-semibold mb-3">
          {filterRole === 'all' ? 'All Users' : `${ROLE_META[filterRole]?.label || filterRole}`}
          <span className="text-sm font-normal text-gray-500 ml-2">
            ({filtered.length})
          </span>
        </h2>
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : (
          <DataTable columns={columns} data={data} />
        )}
      </div>

      {/* Reset Password Modal */}
      {isAdmin && (
        <Modal
          isOpen={!!resetModal}
          onClose={() => setResetModal(null)}
          title={`Reset Password for ${resetModal?.email || ''}`}
          onSave={() => {
            const input = document.getElementById('new-password-input');
            if (input && input.value.length >= 8) {
              resetPassword(resetModal.userId, input.value);
            } else {
              alert('Password must be at least 8 characters');
            }
          }}
          saveLabel="Reset"
        >
          <div className="space-y-2">
            <label className="block text-sm font-medium">New Password (min 8 chars)</label>
            <input
              id="new-password-input"
              type="password"
              className="w-full p-2 border rounded"
              placeholder="Enter new password"
            />
          </div>
        </Modal>
      )}
    </div>
  );
}